import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import { Archive } from '@styled-icons/heroicons-outline/Archive'
import { PencilAlt } from '@styled-icons/heroicons-outline/PencilAlt'
import { Trash } from '@styled-icons/heroicons-outline/Trash'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import { useDeleteCSNoteMutation, usePatchCSNoteMutation } from 'api/notes/configuredServiceNotesCrud'
import { ReactComponent as ThumbtackOutlinedIcon } from 'assets/ThumbtackOutlined.svg'
import { ReactComponent as TruckHiddenOutlinedIcon } from 'assets/TruckHiddenOutlined.svg'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import { putIsLoading } from 'middleware/actions/response'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'

const HHConfirmDialogUntyped = HHConfirmDialog as React.ElementType

const MENU_ANCHOR_ORIGIN = { vertical: 'bottom', horizontal: 'right' } as const
const MENU_TRANSFORM_ORIGIN = { vertical: 'top', horizontal: 'right' } as const
const MENU_SLOT_PROPS = { paper: { variant: 'outlined' as const, sx: { minWidth: 300 } } }
const CONFIRM_DIALOG_PROPS = { maxWidth: 'xs' as const, fullWidth: true }

const SERVICE_NOTE_ONLY = { updateServiceNote: true, updateInherited: false }
const DELETE_SERVICE_NOTE_ONLY = { deleteServiceNote: true, deleteInherited: false }

type SelectedNote = { id?: string; pinned?: boolean; visibleOnMobile?: boolean } & Record<string, unknown>

type NoteActionMenuProps = {
  id: string
  anchorEl?: HTMLElement | null
  showDeleteOption?: boolean
  selectedNote?: SelectedNote
  onEditClick: () => void
  onClose: () => void
}

const NoteActionMenu = ({
  id,
  anchorEl = null,
  showDeleteOption = false,
  selectedNote = {},
  onEditClick,
  onClose,
}: NoteActionMenuProps) => {
  const dispatch = useDispatch()
  const [patchCSNote, { isLoading: isPatchCSNoteLoading }] = usePatchCSNoteMutation()
  const [deleteCSNote, { isLoading: isDeleteCSNoteLoading }] = useDeleteCSNoteMutation()

  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const noteId = get(selectedNote, 'id')
  const isNotePinned = get(selectedNote, 'pinned', false)
  const isVisibleOnMobile = get(selectedNote, 'visibleOnMobile', false)

  const handleEditClick = useCallback(() => {
    onClose()
    onEditClick()
  }, [onClose, onEditClick])

  const handlePinUnpin = useCallback(() => {
    dispatch(putIsLoading(true))
    onClose()
    patchCSNote({ id, noteId, data: { pinned: !isNotePinned } })
      .unwrap()
      .then(() => toast.success(`Note ${isNotePinned ? 'unpinned' : 'pinned'} successfully`))
      .catch(handleError)
      .finally(() => dispatch(putIsLoading(false)))
  }, [dispatch, id, isNotePinned, noteId, onClose, patchCSNote])

  const handleToggleMobileVisibility = useCallback(() => {
    onClose()
    patchCSNote({ id, noteId, data: { visibleOnMobile: !isVisibleOnMobile, ...SERVICE_NOTE_ONLY } })
      .unwrap()
      .catch(handleError)
  }, [id, isVisibleOnMobile, noteId, onClose, patchCSNote])

  const handleCloseArchiveDialog = useCallback(() => setIsArchiveDialogOpen(false), [])

  const handleOpenArchiveDialog = useCallback(() => {
    onClose()
    setIsArchiveDialogOpen(true)
  }, [onClose])

  const handleArchive = useCallback(() => {
    patchCSNote({ id, noteId, data: { archived: true, ...SERVICE_NOTE_ONLY } })
      .unwrap()
      .then(() => {
        toast.success('Note archived successfully')
        handleCloseArchiveDialog()
      })
      .catch(handleError)
  }, [handleCloseArchiveDialog, id, noteId, patchCSNote])

  const handleCloseDeleteDialog = useCallback(() => setIsDeleteDialogOpen(false), [])

  const handleOpenDeleteDialog = useCallback(() => {
    onClose()
    setIsDeleteDialogOpen(true)
  }, [onClose])

  const handleDelete = useCallback(() => {
    deleteCSNote({ id, noteId, data: DELETE_SERVICE_NOTE_ONLY })
      .unwrap()
      .then(() => {
        toast.success('Note deleted successfully')
        handleCloseDeleteDialog()
      })
      .catch(handleError)
  }, [deleteCSNote, handleCloseDeleteDialog, id, noteId])

  return (
    <>
      <Menu
        disableScrollLock
        anchorEl={anchorEl}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        open={Boolean(anchorEl)}
        slotProps={MENU_SLOT_PROPS}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
        onClose={onClose}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon sx={{ mr: 1 }}>
            <PencilAlt height={24} width={24} />
          </ListItemIcon>
          <ListItemText>{T.EDIT}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePinUnpin}>
          <ListItemIcon sx={{ mr: 1 }}>
            <ThumbtackOutlinedIcon />
          </ListItemIcon>
          <ListItemText>{isNotePinned ? T.UNPIN : T.PIN}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleMobileVisibility}>
          <ListItemIcon sx={{ mr: 1 }}>
            <TruckHiddenOutlinedIcon />
          </ListItemIcon>
          <ListItemText>{T.SHOW_ON_MOBILE}</ListItemText>
          <Switch checked={isVisibleOnMobile} edge="end" size="small" tabIndex={-1} />
        </MenuItem>
        <MenuItem sx={{ color: 'warning.main' }} onClick={handleOpenArchiveDialog}>
          <ListItemIcon sx={{ mr: 1, color: 'warning.main' }}>
            <Archive height={24} width={24} />
          </ListItemIcon>
          <ListItemText>{T.ARCHIVE}</ListItemText>
        </MenuItem>
        {showDeleteOption && (
          <MenuItem sx={{ color: 'error.main' }} onClick={handleOpenDeleteDialog}>
            <ListItemIcon sx={{ mr: 1, color: 'error.main' }}>
              <Trash height={24} width={24} />
            </ListItemIcon>
            <ListItemText>{T.DELETE}</ListItemText>
          </MenuItem>
        )}
      </Menu>
      <HHConfirmDialogUntyped
        cancelButtonTitle={T.CANCEL}
        ConfirmButtonProps={{ loading: isPatchCSNoteLoading, color: 'warning' }}
        confirmButtonTitle={T.ARCHIVE}
        confirmDescription={T.ARCHIVE_SERVICE_NOTE_CONFIRMATION_DESCRIPTION}
        confirmTitle={T.ARCHIVE_SERVICE_NOTE}
        DialogProps={CONFIRM_DIALOG_PROPS}
        isOpen={isArchiveDialogOpen}
        onClose={handleCloseArchiveDialog}
        onConfirm={handleArchive}
      />
      <HHConfirmDialogUntyped
        cancelButtonTitle={T.CANCEL}
        ConfirmButtonProps={{ loading: isDeleteCSNoteLoading }}
        confirmButtonTitle={T.DELETE}
        confirmDescription={T.DELETE_SERVICE_NOTE_CONFIRMATION_DESCRIPTION}
        confirmTitle={T.DELETE_SERVICE_NOTE}
        DialogProps={CONFIRM_DIALOG_PROPS}
        isOpen={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDelete}
      />
    </>
  )
}

export default NoteActionMenu
